import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Plus, Pencil, Trash2, Star, Loader2, Phone, Mail, Building } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

interface Contact {
  id: string;
  name: string;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  department?: string | null;
  isPrimary: boolean;
  notes?: string | null;
}

interface ContactFormData {
  name: string;
  title?: string;
  email?: string;
  phone?: string;
  department?: string;
  isPrimary: boolean;
  notes?: string;
}

interface ContactsCardProps {
  customerId: string;
  contacts: Contact[];
}

export function ContactsCard({ customerId, contacts }: ContactsCardProps) {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);

  const utils = trpc.useUtils();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactFormData>({
    defaultValues: {
      name: '',
      title: '',
      email: '',
      phone: '',
      department: '',
      isPrimary: false,
      notes: '',
    },
  });

  const addMutation = trpc.tradingCustomer.addContact.useMutation({
    onSuccess: () => {
      utils.tradingCustomer.byId.invalidate(customerId);
      showSuccessToast(t('customers.contacts.contactAdded'));
      setModalOpen(false);
      reset();
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const updateMutation = trpc.tradingCustomer.updateContact.useMutation({
    onSuccess: () => {
      utils.tradingCustomer.byId.invalidate(customerId);
      showSuccessToast(t('customers.contacts.contactUpdated'));
      setModalOpen(false);
      setSelectedContact(null);
      reset();
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const deleteMutation = trpc.tradingCustomer.deleteContact.useMutation({
    onSuccess: () => {
      utils.tradingCustomer.byId.invalidate(customerId);
      showSuccessToast(t('customers.contacts.contactDeleted'));
      setDeleteDialogOpen(false);
      setSelectedContact(null);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const openModal = (contact?: Contact) => {
    if (contact) {
      setSelectedContact(contact);
      reset({
        name: contact.name,
        title: contact.title || '',
        email: contact.email || '',
        phone: contact.phone || '',
        department: contact.department || '',
        isPrimary: contact.isPrimary,
        notes: contact.notes || '',
      });
    } else {
      setSelectedContact(null);
      reset({
        name: '',
        title: '',
        email: '',
        phone: '',
        department: '',
        isPrimary: false,
        notes: '',
      });
    }
    setModalOpen(true);
  };

  const openDeleteDialog = (contact: Contact) => {
    setSelectedContact(contact);
    setDeleteDialogOpen(true);
  };

  const onSubmit = (data: ContactFormData) => {
    const payload = {
      name: data.name,
      title: data.title || null,
      email: data.email || null,
      phone: data.phone || null,
      department: data.department || null,
      isPrimary: data.isPrimary,
      notes: data.notes || null,
    };

    if (selectedContact) {
      updateMutation.mutate({ id: selectedContact.id, ...payload });
    } else {
      addMutation.mutate({ customerId, ...payload });
    }
  };

  const onDelete = () => {
    if (selectedContact) {
      deleteMutation.mutate(selectedContact.id);
    }
  };

  const isSubmitting = addMutation.isPending || updateMutation.isPending;
  const isDeleting = deleteMutation.isPending;

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>{t('customers.contacts.title')}</CardTitle>
            <CardDescription>
              {t('customers.contacts.description')}
            </CardDescription>
          </div>
          <Button size="sm" onClick={() => openModal()}>
            <Plus className="h-4 w-4 mr-2" />
            {t('customers.contacts.addContact')}
          </Button>
        </CardHeader>
        <CardContent>
          {contacts.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t('customers.contacts.noContacts')}
            </p>
          ) : (
            <div className="space-y-4">
              {contacts.map((contact) => (
                <div
                  key={contact.id}
                  className="flex items-start justify-between p-4 border rounded-lg"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{contact.name}</span>
                      {contact.isPrimary && (
                        <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                      )}
                    </div>
                    {contact.title && (
                      <p className="text-sm text-muted-foreground">
                        {contact.title}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-4 mt-2 text-sm text-muted-foreground">
                      {contact.email && (
                        <div className="flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          <a
                            href={`mailto:${contact.email}`}
                            className="hover:underline"
                          >
                            {contact.email}
                          </a>
                        </div>
                      )}
                      {contact.phone && (
                        <div className="flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          <a
                            href={`tel:${contact.phone}`}
                            className="hover:underline"
                          >
                            {contact.phone}
                          </a>
                        </div>
                      )}
                      {contact.department && (
                        <div className="flex items-center gap-1">
                          <Building className="h-3 w-3" />
                          <span>{contact.department}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => openModal(contact)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => openDeleteDialog(contact)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedContact
                ? t('customers.contacts.editContact')
                : t('customers.contacts.addContact')}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">
                  {t('customers.contacts.name')} *
                </Label>
                <Input
                  id="name"
                  {...register('name', { required: tCommon('required') })}
                />
                {errors.name && (
                  <p className="text-sm text-red-500">{errors.name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="title">{t('customers.contacts.contactTitle')}</Label>
                <Input id="title" {...register('title')} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('customers.contacts.email')}</Label>
                <Input id="email" type="email" {...register('email')} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">{t('customers.contacts.phone')}</Label>
                <Input id="phone" {...register('phone')} />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="department">
                {t('customers.contacts.department')}
              </Label>
              <Input id="department" {...register('department')} />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isPrimary"
                {...register('isPrimary')}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="isPrimary">
                {t('customers.contacts.isPrimary')}
              </Label>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">{t('customers.contacts.notes')}</Label>
              <Textarea id="notes" {...register('notes')} rows={3} />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setModalOpen(false)}
                disabled={isSubmitting}
              >
                {tCommon('cancel')}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {selectedContact ? tCommon('save') : tCommon('create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('customers.contacts.deleteConfirmTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('customers.contacts.deleteConfirmDescription', {
                name: selectedContact?.name,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              {tCommon('cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {tCommon('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
